// What a pre-execution guard saves a scheduled automation: the agent runs only when the guard's
// cheap check finds something to do.
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { guardSavings } from "../../tools/guard";
import { dollars } from "../../tools/meter";
import { useTitle } from "../../ui/hooks";
import { PageHero } from "../../ui/Section";
import { Big, NumberField } from "./Field";

export function GuardSavings() {
  useTitle("Guard savings");
  const [everyMinutes, setEvery] = useState(60);
  const [busyPercent, setBusy] = useState(5);
  const [idleTokens, setIdle] = useState(20000);
  const [busyTokens, setBusyTokens] = useState(80000);
  const [rate, setRate] = useState(6);
  const r = useMemo(() => guardSavings({ everyMinutes, busyPercent, idleTokens, busyTokens, rate }), [everyMinutes, busyPercent, idleTokens, busyTokens, rate]);
  const n = (x: number) => x.toLocaleString("en-GB");

  return (
    <>
      <PageHero kicker="§ Free tools · Guard savings" title={<>No task, no tokens.</>}>
        A scheduled automation pays for every run, even the ones that find nothing. A pre-execution guard asks a cheap, plain question first ("is there anything new?") and only
        wakes the agent when the answer is yes.
      </PageHero>
      <section className="wrap grid gap-8 py-12 lg:grid-cols-[1fr_1fr]">
        <div className="grid gap-5 sm:grid-cols-2">
          <NumberField label="Runs every" value={everyMinutes} onChange={setEvery} min={1} suffix="minutes" />
          <NumberField label="Runs with work to do" hint="How often the check finds something" value={busyPercent} onChange={setBusy} suffix="%" />
          <NumberField label="Tokens when idle" hint="An agent run that finds nothing" value={idleTokens} onChange={setIdle} step={1000} suffix="tokens" />
          <NumberField label="Tokens when busy" hint="A run that does real work" value={busyTokens} onChange={setBusyTokens} step={1000} suffix="tokens" />
          <NumberField label="Blended rate" hint="Input and output averaged, from your provider" value={rate} onChange={setRate} step={0.5} suffix="$ / M" />
          <p className="self-end text-[12.5px] text-ink3">The numbers start as an illustration. Put in your own: your provider's usage page shows what a run really spends.</p>
        </div>
        <aside className="card p-6 sm:p-8" aria-live="polite">
          <div className="grid gap-6 sm:grid-cols-2">
            <Big label="Without a guard" value={`${dollars(r.costWithout)}/mo`} />
            <Big label="With a guard" value={`${dollars(r.costWith)}/mo`} />
          </div>
          <div className="mt-6 rounded-2xl bg-ink p-6 text-paper">
            <p className="font-mono text-[11px] uppercase tracking-wider text-paper/60">Saved</p>
            <p className="display mt-1 text-5xl text-signal">{dollars(r.saved)}</p>
            <p className="mt-1 text-paper/75">{r.savedPercent.toFixed(0)}% of the bill, every month</p>
          </div>
          <p className="mt-6 text-[14.5px] text-ink2">
            {n(r.runsPerMonth)} runs a month: the agent wakes for {n(r.busyRuns)} of them and the guard skips {n(r.skippedRuns)}, each costing a few lines of code and no tokens.
          </p>
          <p className="mt-4 text-[13.5px] text-ink3">
            A guard decides <em>whether</em> you pay, not how much. Read <Link to="/blog/ai-automation-and-the-way-forward" className="link">AI Automation and the Way Forward</Link>.
          </p>
        </aside>
      </section>
    </>
  );
}
